/** @file validateDoc rules for direction sets: mirror sources are authored counterparts; fixes use real ids. */
import type { Doc } from './schema';
import type { DirectionEntry, DirectionsDoc } from './schemaParts/animation';
import type { NeedRef } from './refChecker';
import { drawOrderOpSlots } from './drawOrderOpSlots';
import { names, type Dir } from '../names';
import { makeError, type Pose2DError } from '../pose2dError';

type Entries = DirectionsDoc['entries'];

/** The horizontal counterpart a mirrored direction must use as its source; N and S have none (§5.8). */
const COUNTERPART: Partial<Record<Dir, Dir>> = { E: 'W', W: 'E', NE: 'NW', NW: 'NE', SE: 'SW', SW: 'SE' };

function checkSource(entries: Entries, dir: Dir, entry: DirectionEntry, out: Pose2DError[]): void {
  if (entry.mode === 'authored') return;
  const path = `directions.entries.${dir}.source`;
  const expected = COUNTERPART[dir];
  if (expected === undefined) { out.push(makeError('VALIDATION', {}, { path, hint: `${dir} cannot be mirrored; use authored` })); return; }
  if (entry.source !== expected) { out.push(makeError('VALIDATION', {}, { path, hint: `${dir} may only mirror ${expected}` })); return; }
  if (entries[expected]?.mode !== 'authored') out.push(makeError('VALIDATION', {}, { path, hint: `${dir} mirrors ${expected}, which must be an authored direction of the set` }));
}

function checkFix(dir: Dir, entry: DirectionEntry, need: NeedRef): void {
  const fix = entry.fix;
  if (!fix) return;
  const at = `directions.entries.${dir}.fix`;
  if (fix.skin !== undefined) need(fix.skin, 'skin', `${at}.skin`);
  (fix.drawOrderOps ?? []).forEach((op, i) => { for (const slot of drawOrderOpSlots(op)) need(slot, 'slot', `${at}.drawOrderOps.${i}`); });
  for (const [base, animId] of Object.entries(fix.animations ?? {})) need(animId, 'animation', `${at}.animations.${base}`);
}

/** Appends errors for mirrored directions without a valid source and for fixes that reference missing ids. */
export function checkDocDirections(doc: Doc, need: NeedRef, out: Pose2DError[]): void {
  const entries: Entries = doc.directions?.entries ?? {};
  for (const dir of names.DIRS) {
    const entry = entries[dir];
    if (!entry) continue;
    checkSource(entries, dir, entry, out);
    checkFix(dir, entry, need);
  }
}
